import React from 'react';
import { Servicio } from '../types';
import { Code, Calendar, Tag } from 'lucide-react';

interface ServicioCardProps {
  servicio: Servicio;
  layoutMode: 'grid' | 'list';
  onClick?: () => void;
}

const ServicioCard: React.FC<ServicioCardProps> = ({ servicio, layoutMode, onClick }) => {
  const cardClasses = layoutMode === 'grid'
    ? "card cursor-pointer p-6 transition-all"
    : "card cursor-pointer p-4 transition-all flex items-center gap-4";

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={cardClasses} onClick={onClick}>
      {/* Header */}
      <div className={layoutMode === 'grid' ? "mb-4" : "flex-1"}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center"
              style={{ color: servicio.unidadPrimaria.color || '#2563EB' }}
            >
              <Code size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-0.5">
                {servicio.tecnologia}
              </h3>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Tecnología Principal
              </p>
            </div>
          </div>

          {servicio.categoria && (
            <span className="badge text-xs flex-shrink-0">
              {servicio.categoria}
            </span>
          )}

          {servicio.activo && (
            <div className="w-2 h-2 bg-success rounded-full flex-shrink-0"></div>
          )}
        </div>

        {/* Area */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: servicio.unidadPrimaria.color || '#2563EB' }}
          ></span>
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {servicio.unidadPrimaria.nombre}
          </span>
        </div>

        {/* Description */}
        {servicio.casosUso && layoutMode === 'grid' && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 line-clamp-2">
            {servicio.casosUso}
          </p>
        )}

        {/* Secondary Affinity */}
        {servicio.afinidadSecundaria && (
          <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-3">
            <Tag size={14} className="text-neutral-400 dark:text-neutral-500" />
            <span className="line-clamp-1">{servicio.afinidadSecundaria}</span>
          </div>
        )}

        {/* Support Areas */}
        {(servicio.areasSoporteList?.length || servicio.areasSoporte) && (
          <div className="mb-3">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
              Áreas de Soporte
            </p>
            <div className="flex flex-wrap gap-1.5">
              {servicio.areasSoporteList?.length ? (
                servicio.areasSoporteList.map((area) => (
                  <span
                    key={area.id}
                    className="badge text-xs"
                  >
                    {area.nombre}
                  </span>
                ))
              ) : Array.isArray(servicio.areasSoporte) ? (
                servicio.areasSoporte.map((area) => (
                  <span
                    key={area.id}
                    className="badge text-xs"
                  >
                    {area.nombre}
                  </span>
                ))
              ) : (
                <span className="badge text-xs">
                  {servicio.areasSoporte}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={layoutMode === 'grid'
        ? "pt-4 border-t border-neutral-200 dark:border-neutral-700"
        : "flex items-center"
      }>
        <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          <Calendar size={14} className="text-neutral-400 dark:text-neutral-500" />
          <span>{formatDate(servicio.fechaCreacion)}</span>
        </div>
      </div>
    </div>
  );
};

export default ServicioCard;
